import React, { useState, useEffect } from 'react';
import { Card, Button, Switch, message, Typography, Row, Col, Space, Layout } from 'antd';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import { supabase } from './supabaseClient';

const { Title, Text } = Typography;
const { Content } = Layout;

const Subscription = ({ user }) => {
    const [isAnnual, setIsAnnual] = useState(true);
    const [loading, setLoading] = useState(false);
    const [currentPlan, setCurrentPlan] = useState('free');

    useEffect(() => {
        const fetchSubscription = async () => {
            try {
                const { data, error } = await supabase
                    .from('user_subscriptions')
                    .select('subscription_tier, status')
                    .eq('user_id', user.id)
                    .single();

                if (error) throw error;

                if (data && data.status === 'active') {
                    setCurrentPlan(data.subscription_tier === 'premium' ? 'pro' : 'free');
                }
            } catch (error) {
                console.error('Error fetching subscription:', error);
            }
        };

        fetchSubscription();
    }, [user.id]);

    const handleSubscribe = async (plan) => {
        if (plan === 'pro') {
            setLoading(true);
            try {
                const { data, error } = await supabase.functions.invoke('checkout-session', {
                    body: {
                        user_id: user.id,
                        email: user.email,
                        product_price_id: isAnnual ? "4c3e6aa8-ab21-4136-9642-17b5fa3020c3" : "329d79cc-864b-48ff-8fcd-d7fd5c7f69a9"
                    }
                });

                if (error) throw error;

                if (data?.url) {
                    window.location.href = data.url;
                } else {
                    message.error('Failed to create checkout session');
                }
            } catch (error) {
                console.error('Error:', error);
                message.error('Something went wrong');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleUnsubscribe = () => {
        window.location.href = 'https://polar.sh/elimeeting/portal/';
    };

    const renderFreeButton = () => {
        if (currentPlan === 'free') {
            return (
                <Button
                    type="default"
                    size="large"
                    block
                    disabled
                    style={{ borderRadius: '8px', height: '48px' }}
                >
                    Current Plan
                </Button>
            );
        }
        return null;
    };

    const renderProButton = () => {
        if (currentPlan === 'pro') {
            return (
                <Button
                    type="primary"
                    size="large"
                    block
                    disabled
                    style={{
                        borderRadius: '8px',
                        height: '48px',
                        background: 'linear-gradient(45deg, #1890ff, #52c41a)',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}
                >
                    Current Plan
                </Button>
            );
        }
        return (
            <Button
                type="primary"
                size="large"
                block
                onClick={() => handleSubscribe('pro')}
                loading={loading}
                style={{
                    borderRadius: '8px',
                    height: '48px',
                    background: 'linear-gradient(45deg, #1890ff, #52c41a)',
                    border: 'none',
                    fontSize: '16px',
                    fontWeight: 'bold'
                }}
            >
                Upgrade to Pro
            </Button>
        );
    };

    return (
        <Layout style={{ background: '#f7f9fc' }}>
            <Content style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 24px' }}>
                <Title level={1} style={{
                    textAlign: 'center',
                    marginBottom: '24px',
                    background: 'linear-gradient(45deg, #1890ff, #52c41a)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    Choose Your Plan
                </Title>

                <Text style={{
                    display: 'block',
                    textAlign: 'center',
                    fontSize: '16px',
                    color: '#666',
                    marginBottom: '48px'
                }}>
                    Select the perfect plan for your needs
                </Text>

                <Space align="center" style={{
                    width: '100%',
                    justifyContent: 'center',
                    marginBottom: '48px',
                    background: '#fff',
                    padding: '12px',
                    borderRadius: '30px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}>
                    <Text type={!isAnnual ? "secondary" : undefined}>Monthly</Text>
                    <Switch
                        checked={isAnnual}
                        onChange={setIsAnnual}
                        style={{ background: isAnnual ? '#52c41a' : undefined }}
                    />
                    <Text type={isAnnual ? "success" : "secondary"}>
                        Annual (Save 38%)
                    </Text>
                </Space>

                <Row gutter={32} justify="center">
                    <Col xs={24} sm={12}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                height: '100%'
                            }}
                        >
                            <Title level={2} style={{ color: '#1890ff' }}>Free</Title>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '24px', fontSize: '16px' }}>Perfect for getting started</Text>
                            <Title level={2} style={{ marginBottom: '32px', color: '#262626' }}>
                                $0<Text type="secondary" style={{ fontSize: '16px' }}>/month</Text>
                            </Title>
                            <Space direction="vertical" size="middle" style={{ display: 'block', marginBottom: '32px' }}>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>Unlimited transcript record</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>Meeting timer & note-taking tools</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>3 meetings/month live translation</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>5 meetings/month live summarization</Text>
                                </Space>
                            </Space>
                            {renderFreeButton()}
                        </Card>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 20px rgba(24,144,255,0.15)',
                                border: '2px solid #1890ff',
                                height: '100%'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>Pro</Title>
                                <Text style={{
                                    color: '#fff',
                                    background: 'linear-gradient(45deg, #1890ff, #52c41a)',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    fontWeight: 'bold'
                                }}>Most Popular</Text>
                            </div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '24px', fontSize: '16px' }}>For professional users</Text>
                            <Title level={2} type="success" style={{ marginBottom: '32px', color: '#52c41a' }}>
                                ${isAnnual ? '8' : '13'}<Text type="secondary" style={{ fontSize: '16px' }}>/month</Text>
                            </Title>
                            <Space direction="vertical" size="middle" style={{ display: 'block', marginBottom: '32px' }}>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>Unlimited transcript record</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>Meeting timer & note-taking tools</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>15 meetings/month live translation</Text>
                                </Space>
                                <Space align="start">
                                    <CheckOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                                    <Text>30 meetings/month live summarization</Text>
                                </Space>
                            </Space>
                            {renderProButton()}
                        </Card>
                    </Col>
                </Row>

                {/* Add new card for additional quota */}
                <Row justify="center" style={{ marginTop: '32px' }}>
                    <Col xs={24} sm={16}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                background: 'linear-gradient(to right, #f0f5ff, #e6fffb)',
                                border: '1px dashed #1890ff'
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <Title level={3} style={{ color: '#1890ff', marginBottom: '16px' }}>
                                    Need Additional Quota?
                                </Title>
                                <Text style={{ fontSize: '16px', display: 'block', marginBottom: '24px' }}>
                                    Contact us to purchase additional meeting quotas for your needs
                                </Text>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => window.location.href = 'mailto:elimeetagent@gmail.com?subject=Additional%20Quota%20Request'}
                                    style={{
                                        borderRadius: '8px',
                                        height: '48px',
                                        background: 'linear-gradient(45deg, #1890ff, #52c41a)',
                                        border: 'none',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Request Additional Quota
                                </Button>
                            </div>
                        </Card>
                    </Col>
                </Row>

                <div style={{ textAlign: 'center', marginTop: '48px' }}>
                    <Button
                        type="link"
                        onClick={handleUnsubscribe}
                        style={{ fontSize: '16px' }}
                    >
                        Manage Your Subscription
                    </Button>
                </div>
            </Content>
        </Layout>
    );
};

export default Subscription;